import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { attendanceNote, occupantName } from '../../../lib/seatingCandidates';
import type { SeatAssignment } from '../../../types/seating';

/**
 * What a host can do to a chair that already has someone in it.
 *
 * Move is deliberately two taps and not a drag: arm the move here, then tap the
 * destination chair. A host does this standing in the venue on a phone, and it
 * is the only interaction that works there.
 *
 * Unseating never touches the guest's dinner claim, exactly as releasing a
 * dinner spot never unseats anyone. The two systems only move in one direction,
 * and the host owns the room.
 */
export default function SeatOccupantDialog({
  seatName,
  assignment,
  saving,
  onClose,
  onMove,
  onUnseat,
}: {
  seatName: string;
  assignment: SeatAssignment;
  saving: boolean;
  onClose: () => void;
  onMove: () => void;
  /** Failures surface on the page's alert; the editor closes this dialog for them. */
  onUnseat: () => void;
}) {
  const name = occupantName(assignment);
  const stale = attendanceNote(assignment.attendanceStatus);
  const blocked = assignment.seatKind === 'blocked';

  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 0.5 }}>{name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">{seatName}</Typography>
          {stale ? (
            <Alert severity="warning">
              {name} {stale}. Their chair is kept until you say otherwise — if they claim again it is
              still theirs.
            </Alert>
          ) : null}
          {assignment.seatKind === 'guest' ? (
            <Typography variant="body2" color="text.secondary">
              A plus-one chair. It counts against the party size on their dinner claim.
            </Typography>
          ) : null}
          {assignment.notes ? <Typography variant="body2">{assignment.notes}</Typography> : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        {blocked ? null : (
          <Button onClick={onMove} disabled={saving}>Move to another chair</Button>
        )}
        <Button color="error" onClick={onUnseat} disabled={saving}>
          {blocked ? 'Free this chair' : 'Take them out of this chair'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
